import React, { useEffect, useState } from "react";
import SettingsService from "../../services/SettingsService";

const DEFAULT_PAYMENT_REMINDER_RULES = {
  id: "paymentReminderRules",
  enabled: true,
  firstOverdueReminderDaysAfterDueDate: 2,
  nextOverdueReminderDaysAfterPrevious: 7,
  maxOverdueReminders: 3,
};

const DEFAULT_SCHOOL_CONTACT = {
  id: "schoolContact",
  email: "szkola@extra-english.pl",
  phone: "(+48) 575 400 141",
  address: "Wypoczynkowa 43, 62-081 Baranowo",
  mapsUrl: "https://www.google.com/maps/search/?api=1&query=Extra%20English%20Wypoczynkowa%2043%2062-081%20Baranowo",
};

const DEFAULT_CANCELLATION_POLICY = {
  id: "cancellationPolicy",
  enabled: true,
  cancellationCutoffHours: 24,
};

const DEFAULT_DASHBOARD_PREFERENCES = {
  id: "dashboardPreferences",
  returnStateTtlHours: 6,
};

function byId(settings, id, fallback) {
  return settings.find(setting => setting.id === id) || fallback;
}

function toPositiveInputValue(value) {
  return Number.isInteger(Number(value)) && Number(value) > 0 ? Number(value) : 1;
}

const Settings = () => {
  const [paymentRules, setPaymentRules] = useState(DEFAULT_PAYMENT_REMINDER_RULES);
  const [schoolContact, setSchoolContact] = useState(DEFAULT_SCHOOL_CONTACT);
  const [cancellationPolicy, setCancellationPolicy] = useState(DEFAULT_CANCELLATION_POLICY);
  const [dashboardPreferences, setDashboardPreferences] = useState(DEFAULT_DASHBOARD_PREFERENCES);
  const [loading, setLoading] = useState(true);
  const [savingSection, setSavingSection] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    SettingsService.getSettings()
      .then((settings) => {
        setPaymentRules(byId(settings, "paymentReminderRules", DEFAULT_PAYMENT_REMINDER_RULES));
        setSchoolContact(byId(settings, "schoolContact", DEFAULT_SCHOOL_CONTACT));
        setCancellationPolicy(byId(settings, "cancellationPolicy", DEFAULT_CANCELLATION_POLICY));
        setDashboardPreferences(byId(settings, "dashboardPreferences", DEFAULT_DASHBOARD_PREFERENCES));
        setErrorMessage("");
      })
      .catch(() => {
        setErrorMessage("Nie udalo sie wczytac ustawien.");
      })
      .finally(() => setLoading(false));
  }, []);

  const handlePaymentChange = (event) => {
    const { name, value, type, checked } = event.target;
    setPaymentRules({
      ...paymentRules,
      [name]: type === "checkbox" ? checked : toPositiveInputValue(value),
    });
  };

  const handleSchoolContactChange = (event) => {
    const { name, value } = event.target;
    setSchoolContact({
      ...schoolContact,
      [name]: value,
    });
  };

  const handleCancellationPolicyChange = (event) => {
    const { name, value, type, checked } = event.target;
    setCancellationPolicy({
      ...cancellationPolicy,
      [name]: type === "checkbox" ? checked : toPositiveInputValue(value),
    });
  };

  const handleDashboardPreferencesChange = (event) => {
    const { name, value } = event.target;
    setDashboardPreferences({
      ...dashboardPreferences,
      [name]: toPositiveInputValue(value),
    });
  };

  const save = (sectionName, setting, successText) => {
    setSavingSection(sectionName);
    setSuccessMessage("");
    setErrorMessage("");
    return SettingsService.saveSetting(setting)
      .then((savedSetting) => {
        if (savedSetting.id === "paymentReminderRules") setPaymentRules(savedSetting);
        if (savedSetting.id === "schoolContact") setSchoolContact(savedSetting);
        if (savedSetting.id === "cancellationPolicy") setCancellationPolicy(savedSetting);
        if (savedSetting.id === "dashboardPreferences") setDashboardPreferences(savedSetting);
        setSuccessMessage(successText);
      })
      .catch(() => {
        setErrorMessage("Nie udalo sie zapisac ustawien.");
      })
      .finally(() => setSavingSection(""));
  };

  if (loading) {
    return <div className="manage-text">Ladowanie ustawien...</div>;
  }

  return (
    <div>
      <h2 className="manage-title">Ustawienia</h2>

      {successMessage && <div className="alert alert-success" role="alert">{successMessage}</div>}
      {errorMessage && <div className="alert alert-danger" role="alert">{errorMessage}</div>}

      <div className="card mb-3">
        <div className="card-body">
          <h5 className="card-title">Przypomnienia o platnosciach</h5>
          <div className="form-group form-check">
            <input
              id="payment-reminders-enabled"
              className="form-check-input"
              type="checkbox"
              name="enabled"
              checked={paymentRules.enabled}
              onChange={handlePaymentChange}
            />
            <label className="form-check-label" htmlFor="payment-reminders-enabled">Wlacz wysylke przypomnien</label>
          </div>
          <div className="form-row">
            <div className="form-group col-md-4">
              <label htmlFor="firstOverdueReminderDaysAfterDueDate">Pierwsze przypomnienie po terminie</label>
              <input
                id="firstOverdueReminderDaysAfterDueDate"
                className="form-control"
                type="number"
                min="1"
                name="firstOverdueReminderDaysAfterDueDate"
                value={paymentRules.firstOverdueReminderDaysAfterDueDate}
                onChange={handlePaymentChange}
              />
            </div>
            <div className="form-group col-md-4">
              <label htmlFor="nextOverdueReminderDaysAfterPrevious">Kolejne przypomnienia co dni</label>
              <input
                id="nextOverdueReminderDaysAfterPrevious"
                className="form-control"
                type="number"
                min="1"
                name="nextOverdueReminderDaysAfterPrevious"
                value={paymentRules.nextOverdueReminderDaysAfterPrevious}
                onChange={handlePaymentChange}
              />
            </div>
            <div className="form-group col-md-4">
              <label htmlFor="maxOverdueReminders">Maksymalna liczba przypomnien</label>
              <input
                id="maxOverdueReminders"
                className="form-control"
                type="number"
                min="1"
                name="maxOverdueReminders"
                value={paymentRules.maxOverdueReminders}
                onChange={handlePaymentChange}
              />
            </div>
          </div>
          <button
            type="button"
            className="btn manage-button manage-red-button"
            disabled={savingSection === "payment"}
            onClick={() => save("payment", paymentRules, "Ustawienia przypomnien zostaly zapisane.")}
          >
            Zapisz przypomnienia
          </button>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <h5 className="card-title">Kontakt szkoly</h5>
          <div className="form-row">
            <div className="form-group col-md-6">
              <label htmlFor="schoolContactEmail">Email</label>
              <input
                id="schoolContactEmail"
                className="form-control"
                type="email"
                name="email"
                value={schoolContact.email}
                onChange={handleSchoolContactChange}
              />
            </div>
            <div className="form-group col-md-6">
              <label htmlFor="schoolContactPhone">Telefon</label>
              <input
                id="schoolContactPhone"
                className="form-control"
                type="text"
                name="phone"
                value={schoolContact.phone}
                onChange={handleSchoolContactChange}
              />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="schoolContactAddress">Adres</label>
            <input
              id="schoolContactAddress"
              className="form-control"
              type="text"
              name="address"
              value={schoolContact.address}
              onChange={handleSchoolContactChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="schoolContactMapsUrl">Link do mapy</label>
            <input
              id="schoolContactMapsUrl"
              className="form-control"
              type="text"
              name="mapsUrl"
              value={schoolContact.mapsUrl}
              onChange={handleSchoolContactChange}
            />
          </div>
          <button
            type="button"
            className="btn manage-button manage-red-button"
            disabled={savingSection === "schoolContact"}
            onClick={() => save("schoolContact", schoolContact, "Dane kontaktowe zostaly zapisane.")}
          >
            Zapisz kontakt
          </button>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <h5 className="card-title">Polityka odwolania zajec</h5>
          <div className="form-group form-check">
            <input
              id="cancellation-policy-enabled"
              className="form-check-input"
              type="checkbox"
              name="enabled"
              checked={cancellationPolicy.enabled}
              onChange={handleCancellationPolicyChange}
            />
            <label className="form-check-label" htmlFor="cancellation-policy-enabled">Wlacz polityke odwolania</label>
          </div>
          <div className="form-group">
            <label htmlFor="cancellationCutoffHours">Bezkosztowe odwolanie do ilu godzin przed lekcja</label>
            <input
              id="cancellationCutoffHours"
              className="form-control"
              type="number"
              min="1"
              name="cancellationCutoffHours"
              value={cancellationPolicy.cancellationCutoffHours}
              onChange={handleCancellationPolicyChange}
            />
          </div>
          <button
            type="button"
            className="btn manage-button manage-red-button"
            disabled={savingSection === "cancellationPolicy"}
            onClick={() => save("cancellationPolicy", cancellationPolicy, "Polityka odwolania zostala zapisana.")}
          >
            Zapisz polityke odwolania
          </button>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <h5 className="card-title">Dashboard</h5>
          <div className="form-group">
            <label htmlFor="returnStateTtlHours">Pamiec dashboardu w godzinach</label>
            <input
              id="returnStateTtlHours"
              className="form-control"
              type="number"
              min="1"
              name="returnStateTtlHours"
              value={dashboardPreferences.returnStateTtlHours}
              onChange={handleDashboardPreferencesChange}
            />
          </div>
          <button
            type="button"
            className="btn manage-button manage-red-button"
            disabled={savingSection === "dashboardPreferences"}
            onClick={() => save("dashboardPreferences", dashboardPreferences, "Ustawienia dashboardu zostaly zapisane.")}
          >
            Zapisz dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;
